/**
 * useClientsListStore — état de la page V2 « Clients »
 *
 * Liste paginée côté serveur (offset/limit + total), recherche client-only
 * débouncée, tri par colonne. La recherche annule la requête en vol
 * (AbortController) — comme l'omnisearch.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  listClients,
  type ClientListItem,
  type ClientSortField,
  type ClientStatusFilter,
  type SortOrder,
} from '@/api/clientsApi'

const PAGE_SIZE = 25
const DEBOUNCE_MS = 400

export const useClientsListStore = defineStore('clientsList', () => {
  const q = ref('')
  const page = ref(1) // 1-based
  const pageSize = ref(PAGE_SIZE)
  // Défaut : raison sociale croissante (A→Z).
  const sort = ref<ClientSortField>('name')
  const order = ref<SortOrder>('asc')
  // Filtre statut par défaut : on arrive sur /clients → on veut voir les clients.
  // Bypassé dès qu'il y a une recherche (la recherche porte sur tous les statuts).
  const status = ref<ClientStatusFilter>('client')

  const rows = ref<ClientListItem[]>([])
  const total = ref(0)
  const loading = ref(false)
  const error = ref(false)

  let abort: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)))
  const from = computed(() => (total.value === 0 ? 0 : (page.value - 1) * pageSize.value + 1))
  const to = computed(() => Math.min(page.value * pageSize.value, total.value))

  // Statut envoyé à l'API : aucun si recherche (tous statuts) ou si 'all' sélectionné.
  function effectiveStatus(): string | undefined {
    if (q.value.trim()) return undefined
    return status.value === 'all' ? undefined : status.value
  }

  async function fetch() {
    abort?.abort()
    abort = new AbortController()
    loading.value = true
    error.value = false
    try {
      const res = await listClients({
        q: q.value.trim() || undefined,
        status: effectiveStatus(),
        limit: pageSize.value,
        offset: (page.value - 1) * pageSize.value,
        sort: sort.value,
        order: order.value,
        signal: abort.signal,
      })
      rows.value = res.data
      total.value = res.total
      loading.value = false
    } catch (e: unknown) {
      // AbortError = requête annulée volontairement (nouvelle frappe) → pas une erreur
      if (e instanceof Error && e.name === 'AbortError') return
      error.value = true
      loading.value = false
    }
  }

  function reloadNow() {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    void fetch()
  }

  function setQuery(value: string) {
    q.value = value
    page.value = 1
    if (debounceTimer) clearTimeout(debounceTimer)
    loading.value = true
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void fetch()
    }, DEBOUNCE_MS)
  }

  function setPage(p: number) {
    const next = Math.min(Math.max(1, p), pageCount.value)
    if (next === page.value) return
    page.value = next
    reloadNow()
  }

  /** Sélection d'un statut (puces). Sans effet visible tant qu'une recherche est active. */
  function setStatus(value: ClientStatusFilter) {
    if (status.value === value) return
    status.value = value
    page.value = 1
    reloadNow()
  }

  /** Clic en-tête : même colonne → inverse le sens ; nouvelle colonne → asc. */
  function setSort(field: ClientSortField) {
    if (sort.value === field) {
      order.value = order.value === 'asc' ? 'desc' : 'asc'
    } else {
      sort.value = field
      order.value = 'asc'
    }
    page.value = 1
    reloadNow()
  }

  /** Entrée sur la page : reset + 1er chargement. `initialQuery` vient de /clients?q=… */
  function init(initialQuery?: string) {
    q.value = initialQuery?.trim() ?? ''
    page.value = 1
    sort.value = 'name'
    order.value = 'asc'
    status.value = 'client'
    void fetch()
  }

  return {
    q,
    page,
    pageSize,
    sort,
    order,
    status,
    rows,
    total,
    loading,
    error,
    pageCount,
    from,
    to,
    setQuery,
    setPage,
    setSort,
    setStatus,
    init,
    reload: reloadNow,
  }
})
